import { Routes, Route, Navigate } from 'react-router-dom';
import { Login } from '../pages/Login/Login';
import PrivateRoute from '../helpers/PrivateRoute/PrivateRoute';

import { NotFound } from '../pages/NotFound/NotFound';

import { SignUp } from '../pages/SignUp/SignUp';
import { VerifyEmail } from '../pages/VerifyEmail/VerifyEmail';
import { AcquisitionSource } from '../pages/AcquisitionSource/AcquisitionSource';
import { AcceptTerms } from '../pages/AcceptTerms/AcceptTerms';
import { VerifyPhone } from '../pages/VerifyPhone/VerifyPhone';
import { lazy } from 'react';
import Layout from '../helpers/Layout/Layout';
import { SafeCharge } from '../helpers/utils/types';
import RewardsPage from '../pages/Rewards/RewardsPage/RewardsPage';
import ReferAndEarn from '../pages/ReferAndEarn/ReferAndEarn';
import CreditHealth from '../pages/CreditHealth/CreditHealth';
import PaymentMethods from '../pages/Payment/PaymentMethods/PaymentMethods';
import { ResetPassword } from '../pages/ResetPassword/ResetPassword';
import PaymentPage from '../pages/Payment/PaymentPage/PaymentPage';
import MFAVerification from '../pages/MFA/MFAVerification';
import MFAVerificationPhone from '../pages/MFA/MFAVerificationPhone';
import { SelectVerifyMethod } from '../pages/MFA/SelectVerifyMethod';
import ForgotPasswordLogin from '../pages/ForgotPasswordLogin/ForgotPasswordLogin';
import AddPhoneNumber from '../pages/AddPhoneNumber/AddPhoneNumberPage';
import TenantType from '../pages/TenantType/TenantType';
import BusinessOnboarding from '../pages/KYB/BusinessOnboarding/BusinessOnboarding';
import CompanyInformation from '../pages/KYB/CompanyInformation/CompanyInformation';
import VerifyBusiness from '../pages/KYB/VerifyBusiness/VerifyBusiness';
import AddStakeholders from '../pages/KYB/Stakeholders/AddStakeholders';
import ProfilePage from '../pages/Profile/ProfilePage';
import WalletPage from '../pages/Wallet/WalletPage';
import PrizeDrawPage from '../pages/PrizeDraw/PrizeDrawPage';
import RentReporting from '../pages/RentReporting/RentReporting';
import Dashboard from '../pages/dashboard/Dashboard';

import UnsubscribeEmails from '../pages/UnsubscribeEmails/UnsubscribeEmails';
import SkillTestPage from '../pages/ContestSkillTest/SkillTestPage';
import { NuveiPaymentReturnPage } from '../pages/Payment/NuveiPaymentReturn/NuveiPaymentReturnPage';

const AddProperty = lazy(() => import('../pages/addProperty/AddProperty/AddProperty'));
const PayRentPage = lazy(() => import('../pages/PayRent/PayRentPage'));

const AppRouter = ({ safeCharge }: { safeCharge: SafeCharge }) => {
  return (
    <Routes>
      <Route path='/' element={<Login />} />
      <Route path='/login' element={<Login />} />
      <Route path='/forgot-password' element={<ForgotPasswordLogin />} />
      <Route path='change-password/:hash' element={<ResetPassword />} />
      <Route path='/two-factor-authentication/email' element={<MFAVerification />} />
      <Route path='/select-two-factor-authentication' element={<SelectVerifyMethod />} />
      <Route path='/two-factor-authentication/phone' element={<MFAVerificationPhone />} />
      <Route path='/sign-up' element={<SignUp />} />
      <Route path='/sign-up/:referralCode' element={<SignUp />} />
      <Route path='/unsubscribe' element={<UnsubscribeEmails />} />
      <Route path='/unsubscribe/:token' element={<UnsubscribeEmails />} />
      <Route path='/go/:action' element={<Login />} />
      <Route path='/contest/skill-test' element={<SkillTestPage />} />
      <Route path='/contest/skill-test/:token' element={<SkillTestPage />} />

      <Route
        path='/tenant-type'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
            <TenantType />
          </PrivateRoute>
        }
      />

      <Route
        path='/verify-email'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'mfa']}>
            <VerifyEmail />
          </PrivateRoute>
        }
      />
      <Route
        path='/acquisition-source'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
            <AcquisitionSource />
          </PrivateRoute>
        }
      />
      <Route
        path='/accept-terms'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
            <AcceptTerms />
          </PrivateRoute>
        }
      />
      <Route
        path='/add-phone-number'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'mfa']}>
            <AddPhoneNumber />
          </PrivateRoute>
        }
      />
      <Route
        path='/kyb'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
            <BusinessOnboarding />
          </PrivateRoute>
        }
      >
        <Route index element={<CompanyInformation />} />
        <Route path='verify' element={<VerifyBusiness />} />
        <Route path='stakeholders' element={<AddStakeholders />} />
      </Route>

      <Route
        path='/verify-phone'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
            <VerifyPhone />
          </PrivateRoute>
        }
      />
      <Route
        path='add-property'
        element={
          <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
            <AddProperty />
          </PrivateRoute>
        }
      />
      <Route path='/' element={<Layout />}>
        {' '}
        <Route
          path='dashboard'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path='/pay-rent'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <PayRentPage safeCharge={safeCharge} />
            </PrivateRoute>
          }
        />
        <Route
          path='/pay-now'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <PayRentPage safeCharge={safeCharge} />
            </PrivateRoute>
          }
        />
        {/* Nuvei 3DS return URLs — no PrivateRoute: bank redirect can outlast JWT/MFA.
            Page only reads sessionStorage/query params, then redirects to protected routes. */}
        <Route
          path='payment-success'
          element={<NuveiPaymentReturnPage returnStatus='success' />}
        />
        <Route
          path='payment-failure'
          element={<NuveiPaymentReturnPage returnStatus='failure' />}
        />
        <Route
          path='payment-pending'
          element={<NuveiPaymentReturnPage returnStatus='pending' />}
        />
        <Route
          path='credit-health'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <CreditHealth />
            </PrivateRoute>
          }
        />
        <Route
          path='rent-reporting'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <RentReporting />
            </PrivateRoute>
          }
        />
        <Route
          path='refer'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <ReferAndEarn />
            </PrivateRoute>
          }
        />
        <Route
          path='payment/:paymentType/:tenantBuildingId'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <PaymentPage safeCharge={safeCharge as SafeCharge} />
            </PrivateRoute>
          }
        />
        {/* <Route
          path='edit-property/:tenantBuildingId'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <EditProperty />
            </PrivateRoute>
          }
        />
        <Route
          path='edit-property-manager/:tenantBuildingId'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <EditPropertyManager />
            </PrivateRoute>
          }
        /> */}
        <Route
          path='payment-methods'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <PaymentMethods />
            </PrivateRoute>
          }
        />
        <Route
          path='wallet'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <WalletPage />
            </PrivateRoute>
          }
        />
        <Route path='prize-draw' element={<Navigate to='/rent-drop' replace />} />
        <Route
          path='rent-drop'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <PrizeDrawPage />
            </PrivateRoute>
          }
        />
        <Route
          path='profile'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified', 'mfa']}>
              <ProfilePage />
            </PrivateRoute>
          }
        />
        {/* <Route
                path='/credit-builder/select-building'
                element={
                  <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
                    <GetBuildings />
                  </PrivateRoute>
                }
              />
              <Route
                path='/credit-builder/terms-conditions'
                element={
                  <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
                    <TermsConditions />
                  </PrivateRoute>
                }
              />
              <Route
                path='/credit-builder/process'
                element={
                  <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
                    <EquifaxProcess />
                  </PrivateRoute>
                }
              />
              <Route
                path='/credit-builder/lease-details'
                element={
                  <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
                    <LeaseDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path='/credit-builder/confirmation'
                element={
                  <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
                    <ActivateReporting />
                  </PrivateRoute>
                }
              /> 
              <Route
                path='/credit-builder/settings'
                element={
                  <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
                    <CreditBuilderSettings />
                  </PrivateRoute>
                }
              />
               */}
        <Route
          path='/rewards'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
              <RewardsPage />
            </PrivateRoute>
          }
        />
        <Route
          path='/rewards/earn'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
              <RewardsPage />
            </PrivateRoute>
          }
        />
        <Route
          path='/rewards/referrals'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
              <RewardsPage />
            </PrivateRoute>
          }
        />
        <Route
          path='/rewards/social-submissions'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
              <RewardsPage />
            </PrivateRoute>
          }
        />
        <Route path='/rewards/wallet' element={<Navigate to='/wallet' replace />} />
        <Route
          path='/rewards/contest'
          element={
            <PrivateRoute requiredVerifications={['isTenant', 'isEmailVerified']}>
              <RewardsPage />
            </PrivateRoute>
          }
        />
      </Route>

      <Route path='*' element={<NotFound />} />
    </Routes>
  );
};

export default AppRouter;
